import Phaser from 'phaser';
import {
  COLORS,
  DEPTHS,
  DSO_MODAL,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  toHexColor,
} from '../config/gameConfig';
import { COPY } from '../content/copy';
import type { EventBus } from '../systems/EventBus';
import { propagateDepth } from './propagateDepth';

export interface DsoSource {
  computeDsoSnapshot(): number;
}

export class DsoDashboardModal {
  private container: Phaser.GameObjects.Container | null = null;
  private currentDso = 0;
  private escHandler: ((ev: KeyboardEvent) => void) | null = null;

  private readonly onBeat = (p: { beatId: string }) => {
    if (p.beatId === 'beat-8-dso' && !this.container) this.open();
  };

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly bus: EventBus,
    private readonly tracker: DsoSource,
  ) {
    bus.on('beat:triggered', this.onBeat);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  dismissForTest(): void {
    this.close();
  }

  private open(): void {
    this.currentDso = this.tracker.computeDsoSnapshot();
    this.bus.emit('term:unlocked', { term: 'dso' });
    this.bus.emit('dso:opened', { dso: this.currentDso });

    const x = Math.round((GAME_WIDTH - DSO_MODAL.width) / 2);
    const y = Math.round((GAME_HEIGHT - DSO_MODAL.height) / 2);

    const root = this.scene.add.container(0, 0);
    root.setDepth(DEPTHS.modal);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.black, DSO_MODAL.backdropAlpha)
      .setOrigin(0, 0);
    root.add(backdrop);

    const panel = this.scene.add
      .rectangle(x, y, DSO_MODAL.width, DSO_MODAL.height, COLORS.windowBody)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive();
    root.add(panel);

    const titleText = COPY.dso.modalTitle.replace('{dso}', String(this.currentDso));
    const title = this.scene.add.text(
      x + DSO_MODAL.width / 2,
      y + DSO_MODAL.paddingY,
      titleText,
      {
        fontFamily: FONTS.ui,
        fontSize: DSO_MODAL.titleFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    ).setOrigin(0.5, 0);
    root.add(title);

    const industryY = y + DSO_MODAL.paddingY + title.height + DSO_MODAL.paddingY;
    const industry = this.scene.add.text(
      x + DSO_MODAL.width / 2,
      industryY,
      COPY.dso.industryLine,
      {
        fontFamily: FONTS.ui,
        fontSize: DSO_MODAL.metricFontSize,
        color: toHexColor(COLORS.textMuted),
      },
    ).setOrigin(0.5, 0);
    root.add(industry);

    const targetY = industryY + industry.height + DSO_MODAL.metricGap;
    const target = this.scene.add.text(
      x + DSO_MODAL.width / 2,
      targetY,
      COPY.dso.targetLine,
      {
        fontFamily: FONTS.ui,
        fontSize: DSO_MODAL.metricFontSize,
        color: toHexColor(COLORS.textMuted),
      },
    ).setOrigin(0.5, 0);
    root.add(target);

    const explainerY = targetY + target.height + DSO_MODAL.explainerPaddingY;
    const explainer = this.scene.add.text(
      x + DSO_MODAL.paddingX,
      explainerY,
      COPY.dso.explainer,
      {
        fontFamily: FONTS.ui,
        fontSize: DSO_MODAL.explainerFontSize,
        color: toHexColor(COLORS.textPrimary),
        wordWrap: { width: DSO_MODAL.width - DSO_MODAL.paddingX * 2 },
        align: 'center',
      },
    ).setOrigin(0, 0);
    root.add(explainer);

    const contextY = explainerY + explainer.height + DSO_MODAL.metricGap;
    const context = this.scene.add.text(
      x + DSO_MODAL.paddingX,
      contextY,
      COPY.dso.contextLine,
      {
        fontFamily: FONTS.ui,
        fontSize: DSO_MODAL.contextFontSize,
        color: toHexColor(COLORS.textMuted),
        fontStyle: 'italic',
        wordWrap: { width: DSO_MODAL.width - DSO_MODAL.paddingX * 2 },
        align: 'center',
      },
    ).setOrigin(0, 0);
    root.add(context);

    const buttonY = y + DSO_MODAL.height - DSO_MODAL.paddingY - DSO_MODAL.buttonHeight;
    const buttonWidth = DSO_MODAL.width - DSO_MODAL.paddingX * 2;

    const button = this.scene.add
      .rectangle(
        x + DSO_MODAL.paddingX,
        buttonY,
        buttonWidth,
        DSO_MODAL.buttonHeight,
        COLORS.buttonPrimary,
      )
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    root.add(button);

    const buttonLabel = this.scene.add
      .text(
        x + DSO_MODAL.width / 2,
        buttonY + DSO_MODAL.buttonHeight / 2,
        COPY.dso.dismissButton,
        {
          fontFamily: FONTS.ui,
          fontSize: DSO_MODAL.buttonFontSize,
          color: toHexColor(COLORS.buttonPrimaryText),
          fontStyle: 'bold',
        },
      )
      .setOrigin(0.5, 0.5);
    root.add(buttonLabel);

    button.on('pointerdown', () => this.close());

    this.escHandler = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') this.close();
    };
    this.scene.input.keyboard?.on('keydown', this.escHandler);

    this.container = root;
    propagateDepth(root, DEPTHS.modal);
  }

  private close(): void {
    this.bus.emit('dso:dismissed', { dso: this.currentDso });
    if (this.escHandler) {
      this.scene.input.keyboard?.off('keydown', this.escHandler);
      this.escHandler = null;
    }
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
  }

  private teardown(): void {
    this.bus.off('beat:triggered', this.onBeat);
    if (this.escHandler) {
      this.scene.input.keyboard?.off('keydown', this.escHandler);
      this.escHandler = null;
    }
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
  }
}
